import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { OpenAIDevTools, useOpenAIGlobal, useWidgetProps, useWidgetState, useOpenAIActions } from 'react-openai-apps-sdk';
import { CheckCircle2 } from 'lucide-react';
import { useToolOutputSync } from '@/use-tool-output-sync';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { SafeArea } from '@/components/SafeArea.tsx';
import { CalendarSkeleton } from './components/CalendarSkeleton';
import { CalendarHeader } from './components/CalendarHeader';
import { CalendarGrid } from './components/CalendarGrid';
import { PostList } from './components/PostList';
import { TableView } from './components/TableView';
import { ExportToolbar } from './components/ExportToolbar';
import { PreviewDialog } from './components/dialogs/PreviewDialog';
import { ExportOptionsDialog } from './components/dialogs/ExportOptionsDialog';
import { EmailDialog } from './components/dialogs/EmailDialog';
import { HelpDialog } from './components/dialogs/HelpDialog';
import { PostPreviewDialog } from './components/dialogs/PostPreviewDialog';
// Commented out - using direct scheduling instead of modal (Option 3)
// import { ScheduleModal, type PostWithPage } from './components/dialogs/ScheduleModal';
// import type { ScheduleResult } from './types/schedule';
import { usePostDragDrop } from './hooks/usePostDragDrop';
import { useCalendarExport } from './hooks/useCalendarExport';
import { useEmailSending } from './hooks/useEmailSending';
import { useMockAuth } from './hooks/useMockAuth';
import { useSessionSync } from './hooks/useSessionSync';
import {
  calculateMonthRangeText,
  ensurePostsHaveIds,
  getMonthsToDisplay,
  groupPostsByDate,
  sortPostsByDateTime,
} from './utils/calendar-helpers';
import { generateTestData } from './utils/test-data';
import type { CalendarData, Post, ViewMode } from './types';

const TIPS = [
  'Drag posts to reschedule them in calendar view',
  'Click any post to see full preview with navigation',
  'Use status filters to organize your calendar',
  'Switch between Calendar, List, Combined, and Table views',
  'In Table view, drag rows to reorder posts',
  'Export your calendar as PDF, PNG, or CSV',
  'Use fullscreen mode for better visibility',
  'Send calendar for approval via email'
];

function CalendarWidget() {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [isScheduling, setIsScheduling] = useState(false);

  const authState = useMockAuth();
  const rawData = useWidgetProps<CalendarData>(
    import.meta.env.DEV ? generateTestData : undefined
  );
  const maxHeight = useOpenAIGlobal('maxHeight') as number | undefined;
  const displayMode = useOpenAIGlobal('displayMode') as string | undefined;
  const theme = useOpenAIGlobal('theme') as string | undefined;
  const locale = useOpenAIGlobal('locale') as string | undefined;
  const toolInput = useOpenAIGlobal('toolInput');
  const toolOutput = useOpenAIGlobal('toolOutput');
  const isExpanded = displayMode === 'fullscreen';
  const { sendFollowUpMessage, requestDisplayMode } = useOpenAIActions();

  const [widgetState, setWidgetState] = useWidgetState<{
    sessionId?: string | null;
    posts: Post[] | null;
    viewMode?: ViewMode;
    // scheduleDialogOpen?: boolean; // Commented out - not using modal (Option 3)
    selectedPostId?: string | null;
    // postsToSchedule?: Post[] | null; // Commented out - not using modal (Option 3)
  }>({
    sessionId: null,
    posts: null,
    viewMode: 'calendar',
    // scheduleDialogOpen: false, // Commented out - not using modal (Option 3)
    selectedPostId: null,
    // postsToSchedule: null // Commented out - not using modal (Option 3)
  });

  const viewMode = widgetState?.viewMode || 'calendar';
  // const scheduleDialogOpen = widgetState?.scheduleDialogOpen || false; // Commented out - not using modal (Option 3)

  const rawPosts = rawData?.posts || [];
  const primaryMonth = rawData?.month || '';
  const sessionId = rawData?.sessionId;
  const schedulingEnabled = rawData?._schedulingEnabled ?? false;

  // Use posts from widgetState (user edits) or initial rawPosts from MCP tool
  const allPosts = widgetState?.posts || rawPosts;

  if (import.meta.env.DEV) {
    console.log('[CalendarWidget] Render:', {
      // Full window.openai object
      windowOpenAi: window.openai,

      // OpenAI Globals (individual)
      openaiGlobals: {
        displayMode,
        theme,
        maxHeight,
        locale,
        toolInput,
        toolOutput,
      },

      // Full widgetProps (toolOutput) - complete object
      widgetProps: rawData,

      // Full widgetState - complete object
      widgetState: widgetState,

      // Computed values for quick reference
      summary: {
        toolOutputPostsCount: rawPosts.length,
        widgetStatePostsCount: widgetState?.posts?.length || 0,
        allPostsCount: allPosts.length,
        primaryMonth,
        viewMode,
        isExpanded,
        hasWindowOpenai: !!window.openai,
      }
    });
  }

  // 🔍 USER IDENTIFICATION DETECTION
  useEffect(() => {
    if (window.openai) {
      if (import.meta.env.DEV) {
        console.log('[USER ID DETECTION] All window.openai keys:', Object.keys(window.openai));
        console.log('[USER ID DETECTION] Checking for user-related properties...');
      }

      const searchTerms = ['user', 'id', 'auth', 'email', 'name', 'account', 'profile', 'session', 'token', 'identity'];
      const potentialUserKeys = Object.keys(window.openai).filter(key =>
        searchTerms.some(term => key.toLowerCase().includes(term))
      );

      if (import.meta.env.DEV) {
        console.log('[USER ID DETECTION] Search terms:', searchTerms);
        console.log('[USER ID DETECTION] Found potential user keys:', potentialUserKeys);
      }

      if (potentialUserKeys.length === 0) {
        if (import.meta.env.DEV) {
          console.log('[USER ID DETECTION] ❌ No user-related properties found');
        }
      } else {
        potentialUserKeys.forEach(key => {
          if (import.meta.env.DEV) {
            console.log(`[USER ID DETECTION] ✓ ${key}:`, (window.openai as any)[key]);
          }
        });
      }

      // Also check toolOutput and widgetState for user data
      if (toolOutput) {
        if (import.meta.env.DEV) {
          console.log('[USER ID DETECTION] Checking toolOutput for user data:', toolOutput);
        }
      }
      if (widgetState) {
        if (import.meta.env.DEV) {
          console.log('[USER ID DETECTION] Checking widgetState for user data:', widgetState);
        }
      }
    }
  }, []);

  // Sync widgetState with toolOutput whenever it changes
  // This allows ChatGPT to update the calendar by calling the tool again
  // Uses the reusable hook to prevent the widgetState overwrite bug on page reload
  const handleToolOutputChange = useCallback((newData: CalendarData) => {
    const postsWithIds = ensurePostsHaveIds(newData.posts || []);

    if (import.meta.env.DEV) {
      console.log('[Sync Posts] toolOutput changed → syncing to widgetState:', {
        timestamp: new Date().toISOString(),
        rawPostsLength: newData.posts?.length || 0,
        willUpdateTo: postsWithIds.length,
        sessionId: newData.sessionId,
        firstPost: postsWithIds[0],
      });
    }

    // Use functional form to avoid dependency on widgetState
    setWidgetState((prev) => ({
      ...prev,
      sessionId: newData.sessionId,
      posts: postsWithIds,
    }));
  }, []);

  useToolOutputSync(rawData, handleToolOutputChange);

  // Filter posts by status if filter is active
  const posts = useMemo(() => {
    if (!statusFilter) return allPosts;
    return allPosts.filter(post => post.status === statusFilter);
  }, [allPosts, statusFilter]);

  const displayMonths = useMemo(() => {
    if (!posts || !primaryMonth) return [];
    return getMonthsToDisplay(posts, primaryMonth);
  }, [posts, primaryMonth]);

  const monthRangeText = useMemo(() => {
    return calculateMonthRangeText(displayMonths);
  }, [displayMonths]);

  const sortedPosts = useMemo(() => {
    // In table view, sort by order field
    // In calendar/list views, sort by date/time
    if (viewMode === 'table') {
      return [...posts].sort((a, b) => {
        const orderDiff = (a.order ?? 0) - (b.order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        // Fallback to date/time for stable sort when orders are equal
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.time.localeCompare(b.time);
      });
    }
    return sortPostsByDateTime(posts);
  }, [posts, viewMode]);

  const postsByDate = useMemo(() => {
    return groupPostsByDate(posts);
  }, [posts]);

  const {sensors, handleDragEnd} = usePostDragDrop({
    posts,
    widgetState,
    setWidgetState,
    viewMode,
  });

  // Track selected post by ID instead of index to handle re-sorting
  // This fixes the bug where changing a post's date would show a different post's details
  // because the array gets re-sorted but the index stayed the same.
  // Now we track by stable post ID (generated by backend) and derive the index.
  const selectedPostId = widgetState?.selectedPostId ?? null;

  const selectedPost = useMemo(() => {
    if (!selectedPostId) return null;
    return sortedPosts.find(p => p.id === selectedPostId) || null;
  }, [sortedPosts, selectedPostId]);

  const selectedPostIndex = useMemo(() => {
    if (!selectedPostId) return null;
    const index = sortedPosts.findIndex(p => p.id === selectedPostId);
    return index >= 0 ? index : null;
  }, [sortedPosts, selectedPostId]);

  const handlePostClick = useCallback((post: typeof sortedPosts[0]) => {
    setWidgetState({...widgetState, selectedPostId: post.id});
  }, [widgetState, setWidgetState]);

  const handleNavigatePost = useCallback((direction: 'prev' | 'next') => {
    if (selectedPostIndex === null) return;
    const newIndex = direction === 'next' ? selectedPostIndex + 1 : selectedPostIndex - 1;
    if (newIndex < 0 || newIndex >= sortedPosts.length) return;
    const newPost = sortedPosts[newIndex];
    setWidgetState({...widgetState, selectedPostId: newPost.id});
  }, [selectedPostIndex, sortedPosts, widgetState, setWidgetState]);

  const closePreview = useCallback(() => {
    setWidgetState({...widgetState, selectedPostId: null});
  }, [widgetState, setWidgetState]);

  const {
    isExporting,
    exportSuccess,
    setExportSuccess,
    exportDialogOpen,
    setExportDialogOpen,
    exportDialogType,
    exportDialogData,
    exportDialogEmailData,
    currentUploadedUrl,
    calendarRef,
    handleExportImage,
    handleExportPDF,
    handleExportCSV,
    handleSendEmail,
    handleShareCalendar,
  } = useCalendarExport({posts, displayMonths, primaryMonth, viewMode});

  const {
    directEmailDialogOpen,
    setDirectEmailDialogOpen,
    emailAddress,
    setEmailAddress,
    isSendingEmail,
    emailSendError,
    setEmailSendError,
    setPendingEmailPdf,
    handleConfirmSendEmail,
  } = useEmailSending({posts, primaryMonth});

  const scrollContainerRef = React.useRef<HTMLDivElement>(null!);

  // Sync widget state changes to backend (debounced, auto-saves to database)
  useSessionSync(widgetState?.sessionId || undefined, allPosts, viewMode);

  // === LOGGING: Track openai:set_globals events ===
  useEffect(() => {
    const handleSetGlobals = (event: CustomEvent) => {
      if (import.meta.env.DEV) {
        console.log('[openai:set_globals EVENT] Received at', new Date().toISOString(), {
          eventDetail: event.detail,
          currentToolOutput: window.openai?.toolOutput,
          currentWidgetState: window.openai?.widgetState,
          allGlobals: {
            displayMode: window.openai?.displayMode,
            theme: window.openai?.theme,
            locale: window.openai?.locale,
            maxHeight: window.openai?.maxHeight,
          }
        });
      }
    };

    window.addEventListener('openai:set_globals' as any, handleSetGlobals);
    if (import.meta.env.DEV) {
      console.log('[Calendar Widget] Mounted, listening for openai:set_globals events');
    }

    return () => {
      window.removeEventListener('openai:set_globals' as any, handleSetGlobals);
      if (import.meta.env.DEV) {
        console.log('[Calendar Widget] Unmounted, stopped listening for events');
      }
    };
  }, []);

  // === LOGGING: Track toolOutput changes ===
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[toolOutput CHANGED] At', new Date().toISOString(), {
        newToolOutput: toolOutput,
        postsCount: rawPosts.length,
        month: primaryMonth,
      });
    }
  }, [toolOutput]);

  // === LOGGING: Track widgetState changes ===
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('[widgetState CHANGED] At', new Date().toISOString(), {
        newWidgetState: widgetState,
        postsCount: widgetState?.posts?.length || 0,
        viewMode: widgetState?.viewMode,
      });
    }
  }, [widgetState]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % TIPS.length);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const handleFullscreen = async () => {
    const newMode = isExpanded ? 'inline' : 'fullscreen';
    await requestDisplayMode({
      mode: newMode,
      onError: (error) => {
        console.error('Failed to request fullscreen:', error);
      },
    });
  };

  const handleDirectEmail = async () => {
    if (!exportDialogData) return;
    setPendingEmailPdf(exportDialogData);
    setExportDialogOpen(false);
    setDirectEmailDialogOpen(true);
  };

  const handleEmailSendSuccess = async () => {
    try {
      const result = await handleConfirmSendEmail();
      if (result) {
        setExportSuccess(result);
      }
    } catch (error) {
      console.error('Email send error:', error);
    }
  };

  const handleUpdatePost = (updatedPost: Post) => {
    if (import.meta.env.DEV) {
      console.log('[handleUpdatePost] Updating post:', updatedPost.id, updatedPost);
    }

    // Find and update the post by ID
    const updatedPosts = allPosts.map(post =>
      post.id === updatedPost.id ? updatedPost : post
    );

    if (import.meta.env.DEV) {
      console.log('[handleUpdatePost] Updating widgetState with', updatedPosts.length, 'posts');
    }

    // Update the posts in state
    // The selectedPostId will automatically point to the updated post after re-sorting
    setWidgetState({...widgetState, posts: updatedPosts});
  };

  // ChatGPT batch scheduling (send all posts to ChatGPT to open scheduling app)
  const handleSchedulePostsViaChat = async () => {
    if (import.meta.env.DEV) {
      console.log('[Schedule ChatGPT] Sending', posts.length, 'posts to ChatGPT');
    }
    setIsScheduling(true);
    setExportSuccess(null);

    // Natural prompt that guides ChatGPT to use widgetState.posts data
    const prompt = `Open the scheduling widget with all ${posts.length} posts from the calendar.

The posts are in widgetState.posts and already match the tool's input schema.

Call social_media.schedule_post with:
{
  posts: widgetState.posts
}`;

    await sendFollowUpMessage({
      prompt,
      onSuccess: () => {
        setExportSuccess(`✅ ChatGPT is opening the scheduling app. Select the pages where you want to schedule these ${posts.length} posts.`);
        setTimeout(() => setExportSuccess(null), 5000);
      },
      onError: (error) => {
        console.error('[Schedule ChatGPT] Error:', error);
        setExportSuccess(`⚠ Failed to send to ChatGPT: ${error.message}`);
        setTimeout(() => setExportSuccess(null), 5000);
      },
    });

    setIsScheduling(false);
  };

  const handleScheduleSinglePost = useCallback(async (post: typeof allPosts[0]) => {
    if (import.meta.env.DEV) {
      console.log('[Schedule Single] Sending post to ChatGPT for scheduling:', post.topic);
    }
    setIsScheduling(true);
    setExportSuccess(null);

    // Close preview dialog
    setWidgetState({...widgetState, selectedPostId: null});

    // Ask ChatGPT to open scheduling widget with this single post
    const prompt = `Open the scheduling widget with this post: "${post.topic}" scheduled for ${post.date} at ${post.time} on ${post.platform}.

Call social_media.schedule_post with a single post from the calendar.`;

    await sendFollowUpMessage({
      prompt,
      onSuccess: () => {
        setExportSuccess(`✅ ChatGPT is opening the scheduling app for "${post.topic}". Select the page where you want to schedule this post.`);
        setTimeout(() => setExportSuccess(null), 5000);
      },
      onError: (error) => {
        console.error('[Schedule Single] Error:', error);
        setExportSuccess(`⚠ Failed: ${error.message}`);
        setTimeout(() => setExportSuccess(null), 5000);
      },
    });

    setIsScheduling(false);
  }, [widgetState, setWidgetState, sendFollowUpMessage]);

  if (!posts || !primaryMonth) {
    return <CalendarSkeleton/>;
  }

  return (
    <SafeArea
      className="w-full max-w-6xl mx-auto bg-background"
    >
      <div ref={calendarRef} className="border rounded-lg overflow-hidden bg-card h-full flex flex-col">
        <Tabs
          value={viewMode}
          onValueChange={(mode) => setWidgetState({...widgetState, viewMode: mode as ViewMode})}
          className="h-full flex flex-col"
        >
          <CalendarHeader
            monthRangeText={monthRangeText}
            postsCount={posts.length}
            isExpanded={isExpanded}
            statusFilter={statusFilter}
            tip={TIPS[currentTipIndex]}
            onFullscreenClick={handleFullscreen}
            onStatusFilterChange={setStatusFilter}
          />

          <TabsContent
            value="calendar"
            className="m-0 flex flex-col flex-1 min-h-0"
          >
            <div className="flex-1 overflow-hidden">
              <CalendarGrid
                displayMonths={displayMonths}
                primaryMonth={primaryMonth}
                postsByDate={postsByDate}
                sensors={sensors}
                handleDragEnd={handleDragEnd}
                handlePostClick={handlePostClick}
                scrollContainerRef={scrollContainerRef}
                isExpanded={isExpanded}
              />
            </div>

            <div className={`sticky bottom-0 z-10 flex-shrink-0 border-t bg-background ${isExpanded ? 'p-4' : 'px-2 py-1.5'}`}>
              <ExportToolbar
                isExporting={isExporting || isScheduling}
                isExpanded={isExpanded}
                onExportImage={handleExportImage}
                onExportPDF={handleExportPDF}
                onExportCSV={handleExportCSV}
                onSendEmail={handleSendEmail}
                onShareCalendar={handleShareCalendar}
                onHelpClick={() => setHelpDialogOpen(true)}
                onScheduleChatClick={schedulingEnabled ? handleSchedulePostsViaChat : undefined}
                showScheduleButton={schedulingEnabled}
              />
            </div>
          </TabsContent>

          <TabsContent
            value="list"
            className="m-0 flex flex-col flex-1 min-h-0"
          >
            <div className="flex-1 overflow-hidden">
              <PostList
                posts={sortedPosts}
                onPostClick={(post) => handlePostClick(post)}
                compact={false}
                isExpanded={isExpanded}
              />
            </div>

            <div className={`sticky bottom-0 z-10 flex-shrink-0 border-t bg-background ${isExpanded ? 'p-4' : 'px-2 py-1.5'}`}>
              <ExportToolbar
                isExporting={isExporting || isScheduling}
                isExpanded={isExpanded}
                onExportImage={handleExportImage}
                onExportPDF={handleExportPDF}
                onExportCSV={handleExportCSV}
                onSendEmail={handleSendEmail}
                onShareCalendar={handleShareCalendar}
                onHelpClick={() => setHelpDialogOpen(true)}
                onScheduleChatClick={schedulingEnabled ? handleSchedulePostsViaChat : undefined}
                showScheduleButton={schedulingEnabled}
              />
            </div>
          </TabsContent>

          <TabsContent
            value="calendar+list"
            className="flex flex-col m-0 flex-1 min-h-0"
          >
            <div className="flex flex-1 min-h-0">
              <div className="w-96 border-r h-full overflow-hidden">
                <PostList
                  posts={sortedPosts}
                  onPostClick={(post) => handlePostClick(post)}
                  compact={true}
                  isExpanded={isExpanded}
                />
              </div>
              <div className="flex-1 overflow-hidden">
                <CalendarGrid
                  displayMonths={displayMonths}
                  primaryMonth={primaryMonth}
                  postsByDate={postsByDate}
                  sensors={sensors}
                  handleDragEnd={handleDragEnd}
                  handlePostClick={handlePostClick}
                  scrollContainerRef={scrollContainerRef}
                  isExpanded={isExpanded}
                />
              </div>
            </div>

            <div className={`sticky bottom-0 z-10 flex-shrink-0 border-t bg-background ${isExpanded ? 'p-4' : 'px-2 py-1.5'}`}>
              <ExportToolbar
                isExporting={isExporting || isScheduling}
                isExpanded={isExpanded}
                onExportImage={handleExportImage}
                onExportPDF={handleExportPDF}
                onExportCSV={handleExportCSV}
                onSendEmail={handleSendEmail}
                onShareCalendar={handleShareCalendar}
                onHelpClick={() => setHelpDialogOpen(true)}
                onScheduleChatClick={schedulingEnabled ? handleSchedulePostsViaChat : undefined}
                showScheduleButton={schedulingEnabled}
              />
            </div>
          </TabsContent>

          <TabsContent
            value="table"
            className="m-0 flex flex-col flex-1 min-h-0"
          >
            <div className="flex-1 overflow-hidden">
              <TableView
                posts={sortedPosts}
                sensors={sensors}
                handleDragEnd={handleDragEnd}
                handlePostClick={(post) => handlePostClick(post)}
              />
            </div>

            <div className={`sticky bottom-0 z-10 flex-shrink-0 border-t bg-background ${isExpanded ? 'p-4' : 'px-2 py-1.5'}`}>
              <ExportToolbar
                isExporting={isExporting || isScheduling}
                isExpanded={isExpanded}
                onExportImage={handleExportImage}
                onExportPDF={handleExportPDF}
                onExportCSV={handleExportCSV}
                onSendEmail={handleSendEmail}
                onShareCalendar={handleShareCalendar}
                onHelpClick={() => setHelpDialogOpen(true)}
                onScheduleChatClick={schedulingEnabled ? handleSchedulePostsViaChat : undefined}
                showScheduleButton={schedulingEnabled}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {exportSuccess && (
        <Dialog open={!!exportSuccess} onOpenChange={() => setExportSuccess(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary"/>
                Export Successful
              </DialogTitle>
              <DialogDescription>{exportSuccess}</DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}

      <ExportOptionsDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        exportType={exportDialogType}
        exportData={exportDialogData}
        emailData={exportDialogEmailData}
        uploadedUrl={currentUploadedUrl}
        onShowPreview={() => setPreviewDialogOpen(true)}
        onSendViaEmail={handleDirectEmail}
      />

      <PreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        exportType={exportDialogType}
        exportData={exportDialogData}
        uploadedUrl={currentUploadedUrl}
      />

      <EmailDialog
        open={directEmailDialogOpen}
        emailAddress={emailAddress}
        isSendingEmail={isSendingEmail}
        emailSendError={emailSendError}
        onClose={() => {
          setDirectEmailDialogOpen(false);
          setEmailAddress('');
          setEmailSendError();
        }}
        onEmailChange={setEmailAddress}
        onConfirmSend={handleEmailSendSuccess}
      />

      <HelpDialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />

      {/* Commented out - using direct scheduling instead of modal (Option 3) */}
      {/* <ScheduleModal
        open={scheduleDialogOpen}
        onClose={handleScheduleDialogClose}
        posts={widgetState?.postsToSchedule || allPosts}
        onComplete={handleScheduleComplete}
      /> */}

      <PostPreviewDialog
        selectedPost={selectedPost}
        selectedPostIndex={selectedPostIndex}
        sortedPostsLength={sortedPosts.length}
        onNavigate={handleNavigatePost}
        onClose={closePreview}
        onUpdatePost={handleUpdatePost}
        onSchedule={schedulingEnabled ? handleScheduleSinglePost : undefined}
      />
    </SafeArea>
  );
}

const container = document.getElementById('content-calendar-root')!;
const root = (container as any)._root || createRoot(container);
if (!(container as any)._root) {
  (container as any)._root = root;
}


root.render(
  <>
    <CalendarWidget/>
    {import.meta.env.DEV && <OpenAIDevTools/>}
  </>
);
